import React, { useState, useEffect } from 'react';
import { 
  getVisualizationUrl, 
  getFeatureImportanceData, 
  getModelPerformanceData, 
  generateROCCurve, 
  generateConfusionMatrix,
  getRocImageUrlByModelId,
  getConfusionImageUrlByModelId,
  getTrainingCurvesImageUrlByModelId,
  getRocDataByModelId,
  getConfusionDataByModelId,
  generateRocByModelId,
  generateConfusionByModelId,
  getCorrelationData,
  getDistributionsData,
  generateFeatureImportance,
  generateModelPerformance,
  getFeatureImportanceImageUrlByModelId,
  getModelPerformanceImageUrlByModelId,
  getFeatureImportanceDataByModelId,
  getModelPerformanceDataByModelId,
  getInstanceComparisonImageUrlByModelId,
  exportVisualization,
  downloadFile,
  exportComprehensiveReport,
} from '../api';
import { EmptyState, InfoTooltip, LoadingState, ErrorState } from './shared/UIComponents';
import { useActiveModel } from '../context/ActiveModelContext';

// No client-side charting; rely on server artifacts

// Helper function to get visualization descriptions
const getVisualizationDescription = (key) => {
  const descriptions = {
    'correlation-heatmap': 'Shows relationships between features using color-coded correlation matrix',
    'distributions': 'Displays the distribution of values for each feature to identify patterns and outliers',
    'feature-importance': 'Ranks features by their importance in the model predictions (requires trained model)',
    'model-performance': 'Shows model performance metrics like accuracy, precision, recall, or R² score (requires trained model)',
    'roc-curve': 'Shows the Receiver Operating Characteristic curve to evaluate classification model performance (requires trained classification model)',
    'confusion-matrix': 'Shows the confusion matrix to visualize classification model predictions vs actual values (requires trained classification model)',
    'training-curve': 'Displays the training curve to show model performance over epochs (neural networks only)',
    'instance-comparison': 'Compares model performance on known AD instances vs random baseline (requires trained model)',
    'cluster-scatter': 'Shows K-Means cluster assignments projected onto 2D PCA space (run clustering first)',
  };
  return descriptions[key] || 'Visualization of data insights';
};

function VisualizationDisplay({ datasetName, modelType = null, modelId = null }) {
  const { activeModel } = useActiveModel();
  const [selectedVisualization, setSelectedVisualization] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMessage, setGenerationMessage] = useState('');
  const [vizJson, setVizJson] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [artifactAvailable, setArtifactAvailable] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(null);

  const effectiveDatasetName = (activeModel && activeModel.id === modelId) ? activeModel.dataset_name : datasetName;

  const openInNewTab = (url) => {
    if (!url) return;
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      // ignore
    }
  };

  // Fetch data when the selected visualization changes
  useEffect(() => {
    const fetchData = async () => {
      if (selectedVisualization === 'roc-curve' || selectedVisualization === 'confusion-matrix' || selectedVisualization === 'feature-importance' || selectedVisualization === 'model-performance' || selectedVisualization === 'training-curve' || selectedVisualization === 'instance-comparison') {
        // Reset states for image/json based visualizations
        setVizJson(null);
        setImageUrl(null);
        setImageError(null);
        setError(null);

        // Resolve URLs
        const resolvedImageUrl = (() => {
          // STRICT: When modelId present, always use modelId paths
          if (modelId) {
            if (selectedVisualization === 'training-curve') return getTrainingCurvesImageUrlByModelId(effectiveDatasetName, modelId);
            if (selectedVisualization === 'roc-curve') return getRocImageUrlByModelId(effectiveDatasetName, modelId);
            if (selectedVisualization === 'confusion-matrix') return getConfusionImageUrlByModelId(effectiveDatasetName, modelId);
            if (selectedVisualization === 'feature-importance') return getFeatureImportanceImageUrlByModelId(effectiveDatasetName, modelId);
            if (selectedVisualization === 'instance-comparison') return getInstanceComparisonImageUrlByModelId(effectiveDatasetName, modelId);
            return getModelPerformanceImageUrlByModelId(effectiveDatasetName, modelId);
          }
          // No modelId: use legacy dataset-level
          if (selectedVisualization === 'roc-curve') return getVisualizationUrl('roc-curve', effectiveDatasetName, modelType);
          if (selectedVisualization === 'confusion-matrix') return getVisualizationUrl('confusion-matrix', effectiveDatasetName, modelType);
          if (selectedVisualization === 'feature-importance') return getVisualizationUrl('feature-importance', effectiveDatasetName, modelType);
          return getVisualizationUrl('model-performance', effectiveDatasetName, modelType);
        })();
        setImageUrl(resolvedImageUrl);

        // Load image with timeout handling
        setImageLoading(true);
        const img = new Image();
        const timeoutMs = 10000;
        const timeoutId = setTimeout(() => {
          setImageLoading(false);
          setImageError('Timed out loading image. The artifact may be missing or generation may have failed.');
        }, timeoutMs);
        img.onload = () => {
          clearTimeout(timeoutId);
          setImageLoading(false);
        };
        img.onerror = () => {
          clearTimeout(timeoutId);
          setImageLoading(false);
          setImageError('Failed to load image. This may not be applicable for regression, or the artifact is missing.');
        };
        img.src = resolvedImageUrl;

        // Load JSON for modelId flows
        if (modelId && (selectedVisualization === 'roc-curve' || selectedVisualization === 'confusion-matrix' || selectedVisualization === 'feature-importance' || selectedVisualization === 'model-performance')) {
          setJsonLoading(true);
          const fetchJson = async () => {
            try {
              let jsonData;
              if (selectedVisualization === 'roc-curve') {
                jsonData = await getRocDataByModelId(effectiveDatasetName, modelId);
              } else if (selectedVisualization === 'confusion-matrix') {
                jsonData = await getConfusionDataByModelId(effectiveDatasetName, modelId);
              } else if (selectedVisualization === 'feature-importance') {
                jsonData = await getFeatureImportanceDataByModelId(effectiveDatasetName, modelId);
              } else {
                jsonData = await getModelPerformanceDataByModelId(effectiveDatasetName, modelId);
              }
              // Backend wraps data; keep entire object for download
              setVizJson(jsonData);
            } catch (e) {
              // Treat 404 as not applicable or missing
              setVizJson(null);
              // Only surface error if image also fails
            } finally {
              setJsonLoading(false);
            }
          };
          fetchJson();
        } else {
          setVizJson(null);
          setJsonLoading(false);
        }
      } else {
        // Reset state for other visualizations
        setVizJson(null);
        setImageUrl(null);
        setImageError(null);
        setArtifactAvailable(true);
      }
    };

    fetchData();
  }, [selectedVisualization, effectiveDatasetName, modelType, modelId]);

  const availableVisualizations = [
    { key: 'correlation-heatmap', label: 'Correlation Heatmap' },
    { key: 'distributions', label: 'Data Distributions' },
    { key: 'cluster-scatter', label: 'Cluster Scatter Plot' },
    ...(modelType ? [
      { key: 'feature-importance', label: 'Feature Importance Chart' },
      { key: 'model-performance', label: 'Model Performance Chart' },
      { key: 'roc-curve', label: 'ROC Curve' },
      { key: 'confusion-matrix', label: 'Confusion Matrix' },
      { key: 'training-curve', label: 'Training Curve' },
      { key: 'instance-comparison', label: 'Instance Comparison' },
    ] : [])
  ];

  // Function to generate visualization
  const generateVisualization = async (vizType) => {
    setIsGenerating(true);
    setError(null);
    setGenerationMessage('');

    try {
      let result;
      if (vizType === 'roc-curve') {
        if (modelId) {
          result = await generateRocByModelId(effectiveDatasetName, modelId);
        } else {
          result = await generateROCCurve(effectiveDatasetName, modelType);
        }
      } else if (vizType === 'confusion-matrix') {
        if (modelId) {
          result = await generateConfusionByModelId(effectiveDatasetName, modelId);
        } else {
          result = await generateConfusionMatrix(effectiveDatasetName, modelType);
        }
      } else if (vizType === 'feature-importance') {
        result = await generateFeatureImportance(effectiveDatasetName, modelType);
      } else if (vizType === 'model-performance') {
        result = await generateModelPerformance(effectiveDatasetName, modelType);
      }
      if (result.status === 'not_implemented') {
        setGenerationMessage(result.message);
      } else {
        setGenerationMessage(result.message);
        // Refresh the visualization after generation
        setTimeout(() => {
          setSelectedVisualization(vizType);
        }, 1000);
      }
    } catch (err) {
      setError(`Failed to generate ${vizType}: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPng = () => {
    // Prefer pre-resolved imageUrl (roc/confusion); else use server URLs for others
    let url = imageUrl;
    if (!url && selectedVisualization && effectiveDatasetName) {
      url = getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType);
    }
    if (!url) return;
    openInNewTab(url);
  };
  // Lightweight availability check for artifacts
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!selectedVisualization || !effectiveDatasetName) return;
      try {
        // STRICT: if modelId exists, check only modelId URL; else legacy
        let url = null;
        if (modelId) {
          url = imageUrl;
        } else if (selectedVisualization && effectiveDatasetName) {
          url = getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType);
        }
        if (!url) return;
        // Try HEAD first; some servers may not allow HEAD → 405. In that case, fall back to GET.
        let ok = false;
        try {
          const headRes = await fetch(url, { method: 'HEAD' });
          ok = headRes.ok;
        } catch (e) {
          ok = false;
        }
        if (!ok) {
          try {
            const getRes = await fetch(url, { method: 'GET', cache: 'no-store' });
            ok = getRes.ok;
          } catch (e2) {
            ok = false;
          }
        }
        if (!cancelled) setArtifactAvailable(ok);
      } catch (e) {
        if (!cancelled) setArtifactAvailable(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [selectedVisualization, effectiveDatasetName, modelType, imageUrl, modelId]);


  const handleDownloadJson = async () => {
    // Prefer modelId-scoped JSON when available for all visuals
    let payload = null;
    if (modelId && selectedVisualization) {
      if (vizJson) {
        if (selectedVisualization === 'roc-curve' || selectedVisualization === 'confusion-matrix') {
          payload = vizJson?.data?.artifact ?? vizJson?.data ?? vizJson;
        } else if (selectedVisualization === 'feature-importance' || selectedVisualization === 'model-performance') {
          payload = vizJson?.data ?? vizJson;
        }
      }
      if (!payload) {
        try {
          if (selectedVisualization === 'roc-curve') {
            const res = await getRocDataByModelId(effectiveDatasetName, modelId);
            payload = res?.data?.artifact ?? res?.data ?? res;
          } else if (selectedVisualization === 'confusion-matrix') {
            const res = await getConfusionDataByModelId(effectiveDatasetName, modelId);
            payload = res?.data?.artifact ?? res?.data ?? res;
          } else if (selectedVisualization === 'feature-importance') {
            const res = await getFeatureImportanceDataByModelId(effectiveDatasetName, modelId);
            payload = res?.data ?? res;
          } else if (selectedVisualization === 'model-performance') {
            const res = await getModelPerformanceDataByModelId(effectiveDatasetName, modelId);
            payload = res?.data ?? res;
          }
        } catch (e) {
          // fall through to dataset-level fetch below
        }
      }
    }

    // If we have modelId and are requesting FI/MP and still no payload, stop here to avoid mixing metrics
    if (!payload && modelId && (selectedVisualization === 'feature-importance' || selectedVisualization === 'model-performance')) {
      setError('No model-specific JSON artifact found. Generate artifacts first.');
      return;
    }

    // Fetch JSON data from backend for dataset-level or when no modelId
    if (!payload) {
      try {
        setIsLoading(true);
        setError(null);
        
        if (selectedVisualization === 'correlation-heatmap') {
          payload = await getCorrelationData(effectiveDatasetName);
        } else if (selectedVisualization === 'distributions') {
          payload = await getDistributionsData(effectiveDatasetName);
        } else if (selectedVisualization === 'feature-importance') {
          payload = await getFeatureImportanceData(effectiveDatasetName, modelType);
        } else if (selectedVisualization === 'model-performance') {
          payload = await getModelPerformanceData(effectiveDatasetName, modelType);
        }
      } catch (error) {
        setError(`Failed to fetch JSON data: ${error.message}`);
        return;
      } finally {
        setIsLoading(false);
      }
    }

    if (!payload) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedVisualization}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    // Dataset-level visualizations don't require modelId, but we need it for proper file naming
    const datasetLevelViz = ['correlation-heatmap', 'distributions', 'cluster-scatter'];
    const isDatasetLevel = datasetLevelViz.includes(selectedVisualization);
    
    if (!modelId && !isDatasetLevel) {
      setError('PDF export requires a model ID. Please select a model.');
      return;
    }

    if (!selectedVisualization) {
      setError('Please select a visualization to export.');
      return;
    }

    setExportingPdf(true);
    setError(null);
    setExportSuccess(null);

    try {
      // Map visualization names to API format
      const vizTypeMap = {
        'roc-curve': 'roc',
        'confusion-matrix': 'confusion',
        'feature-importance': 'feature_importance',
        'model-performance': 'model_performance',
        'correlation-heatmap': 'correlation_heatmap',
        'distributions': 'distributions',
        'training-curve': 'training_curves',
        'instance-comparison': 'instance_comparison',
      };

      const vizType = vizTypeMap[selectedVisualization] || selectedVisualization;
      // Use 'dataset' as placeholder modelId for dataset-level visualizations if no model is selected
      const effectiveModelId = modelId || 'dataset';
      const { blob, filename } = await exportVisualization(effectiveModelId, vizType, 'pdf');
      
      // Trigger download
      downloadFile(blob, filename);
      setExportSuccess(`Successfully exported ${selectedVisualization} as PDF`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setExportSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to export PDF: ${err.message}`);
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportComprehensive = async () => {
    if (!modelId) {
      setError('Comprehensive report export requires a model ID. Please select a model.');
      return;
    }

    setExportingPdf(true);
    setError(null);
    setExportSuccess(null);

    try {
      const { blob, filename } = await exportComprehensiveReport(modelId);
      
      // Trigger download
      downloadFile(blob, filename);
      setExportSuccess('Successfully exported comprehensive report');
      
      // Clear success message after 3 seconds
      setTimeout(() => setExportSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to export report: ${err.message}`);
    } finally {
      setExportingPdf(false);
    }
  };


  // Conditionally renders the correct visualization
  const renderVisualization = () => {
    if (isLoading) {
      return <LoadingState message="Loading visualization..." />;
    }
    if (error) {
      return <ErrorState message={error} />;
    }

    switch (selectedVisualization) {
      case 'correlation-heatmap':
        return (
          <InfoTooltip 
            title="Correlation Heatmap" 
            content="Shows relationships between features. Red = positive correlation, Blue = negative correlation, White = no correlation."
          >
            <div className="visualization-display__image-container">
              <a
                href={getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open image in new tab"
                onClick={(e) => {
                  e.preventDefault();
                  openInNewTab(getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType));
                }}
              >
                <img 
                  key={`correlation-heatmap-${effectiveDatasetName}-${Date.now()}`}
                  src={getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType)}
                  alt={selectedVisualization}
                  className="visualization-display__image"
                  onError={() => setError(`Failed to load ${selectedVisualization} image. This visualization may not be available for the current dataset. Try running correlation analysis first.`)}
                  onClick={(e) => {
                    e.preventDefault();
                    openInNewTab(getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType));
                  }}
                />
              </a>
              <div className="visualization-display__actions">
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadPng}
                >
                  Download PNG
                </button>
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadJson}
                  disabled={false}
                  title="No JSON available for this visualization"
                >
                  Download JSON
                </button>
                <button 
                  className="btn btn--primary"
                  onClick={handleExportPdf}
                  disabled={exportingPdf}
                  title="Export as PDF"
                >
                  {exportingPdf ? 'Exporting...' : 'Export PDF'}
                </button>
              </div>
            </div>
          </InfoTooltip>
        );
      case 'distributions':
        return (
          <InfoTooltip 
            title="Data Distributions" 
            content="Shows the distribution of values for each feature. Helps identify patterns, outliers, and data quality issues."
          >
            <div className="visualization-display__image-container">
              <a
                href={getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open image in new tab"
                onClick={(e) => {
                  e.preventDefault();
                  openInNewTab(getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType));
                }}
              >
                <img 
                  key={`distributions-${effectiveDatasetName}-${Date.now()}`}
                  src={getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType)}
                  alt={selectedVisualization}
                  className="visualization-display__image"
                  onError={() => setError(`Failed to load ${selectedVisualization} image. This visualization may not be available for the current dataset. Try running data exploration first.`)}
                  onClick={(e) => {
                    e.preventDefault();
                    openInNewTab(getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType));
                  }}
                />
              </a>
              <div className="visualization-display__actions">
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadPng}
                >
                  Download PNG
                </button>
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadJson}
                  disabled={false}
                  title="No JSON available for this visualization"
                >
                  Download JSON
                </button>
                <button 
                  className="btn btn--primary"
                  onClick={handleExportPdf}
                  disabled={exportingPdf}
                  title="Export as PDF"
                >
                  {exportingPdf ? 'Exporting...' : 'Export PDF'}
                </button>
              </div>
            </div>
          </InfoTooltip>
        );
      case 'cluster-scatter':
        return (
          <InfoTooltip
            title="Cluster Scatter Plot"
            content="Shows sample groupings from K-Means clustering projected onto the first two principal components. Run unsupervised clustering on the Train tab first."
          >
            <div className="visualization-display__image-container">
              <a
                href={getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open image in new tab"
                onClick={(e) => {
                  e.preventDefault();
                  openInNewTab(getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType));
                }}
              >
                <img
                  key={`cluster-scatter-${effectiveDatasetName}-${Date.now()}`}
                  src={getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType)}
                  alt={selectedVisualization}
                  className="visualization-display__image"
                  style={{ maxWidth: '420px', margin: '0 auto', display: 'block' }}
                  onError={() => setError(`Failed to load cluster scatter plot. Run K-Means clustering on the Train tab for "${effectiveDatasetName}" first.`)}
                  onClick={(e) => {
                    e.preventDefault();
                    openInNewTab(getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType));
                  }}
                />
              </a>
              <div className="visualization-display__actions">
                <button
                  className="btn btn--secondary"
                  onClick={handleDownloadPng}
                >
                  Download PNG
                </button>
              </div>
            </div>
          </InfoTooltip>
        );
      case 'feature-importance':
        if (modelId && !imageUrl) {
          return <LoadingState message="Loading feature importance..." />;
        }
        return (
          <InfoTooltip 
            title="Feature Importance" 
            content="Shows which features contribute most to the model's predictions."
          >
            <div className="visualization-display__image-container">
              <a
                href={imageUrl || getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open image in new tab"
                onClick={(e) => {
                  e.preventDefault();
                  openInNewTab(imageUrl || getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType));
                }}
              >
                <img 
                  key={`feature-importance-${effectiveDatasetName}-${modelId || modelType}-${Date.now()}`}
                  src={imageUrl || getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType)}
                  alt={selectedVisualization}
                  className="visualization-display__image"
                  onError={() => setError(`Failed to load ${selectedVisualization} image. Generate artifacts first.`)}
                  onClick={(e) => {
                    e.preventDefault();
                    openInNewTab(imageUrl || getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType));
                  }}
                />
              </a>
              <div className="visualization-display__actions">
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadPng}
                  disabled={!artifactAvailable}
                  title={!artifactAvailable ? 'Not generated yet — click Generate' : ''}
                >
                  Download PNG
                </button>
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadJson}
                  disabled={!artifactAvailable}
                  title={!artifactAvailable ? 'Not generated yet — click Generate' : ''}
                >
                  Download JSON
                </button>
                {modelId && (
                  <button 
                    className="btn btn--primary"
                    onClick={handleExportPdf}
                    disabled={!artifactAvailable || exportingPdf}
                    title={!artifactAvailable ? 'Not generated yet — click Generate' : 'Export as PDF'}
                  >
                    {exportingPdf ? 'Exporting...' : 'Export PDF'}
                  </button>
                )}
              </div>
            </div>
          </InfoTooltip>
        );
      case 'model-performance':
        if (modelId && !imageUrl) {
          return <LoadingState message="Loading model performance..." />;
        }
        return (
          <InfoTooltip 
            title="Model Performance" 
            content="Shows performance metrics like accuracy, precision, recall, F1 or R²/MSE."
          >
            <div className="visualization-display__image-container">
              <a
                href={imageUrl || getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open image in new tab"
                onClick={(e) => {
                  e.preventDefault();
                  openInNewTab(imageUrl || getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType));
                }}
              >
                <img 
                  key={`model-performance-${effectiveDatasetName}-${modelId || modelType}-${Date.now()}`}
                  src={imageUrl || getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType)}
                  alt={selectedVisualization}
                  className="visualization-display__image"
                  onError={() => setError(`Failed to load ${selectedVisualization} image. Generate artifacts first.`)}
                  onClick={(e) => {
                    e.preventDefault();
                    openInNewTab(imageUrl || getVisualizationUrl(selectedVisualization, effectiveDatasetName, modelType));
                  }}
                />
              </a>
              <div className="visualization-display__actions">
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadPng}
                >
                  Download PNG
                </button>
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadJson}
                >
                  Download JSON
                </button>
                {modelId && (
                  <button 
                    className="btn btn--primary"
                    onClick={handleExportPdf}
                    disabled={exportingPdf}
                    title="Export as PDF"
                  >
                    {exportingPdf ? 'Exporting...' : 'Export PDF'}
                  </button>
                )}
              </div>
            </div>
          </InfoTooltip>
        );
      case 'roc-curve':
        return (
          <InfoTooltip 
            title="ROC Curve" 
            content="Shows the Receiver Operating Characteristic curve. The closer the curve is to the top-left corner, the better the model performance. AUC (Area Under Curve) closer to 1.0 indicates better classification performance."
          >
            <div className="visualization-display__image-container">
              {imageLoading && <LoadingState message="Loading ROC image..." />}
              {imageError && <ErrorState message={imageError} />}
              {!imageLoading && !imageError && imageUrl && (
                <a
                  href={imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open image in new tab"
                  onClick={(e) => {
                    e.preventDefault();
                    openInNewTab(imageUrl);
                  }}
                >
                  <img 
                    key={`roc-curve-${effectiveDatasetName}-${modelId || modelType}-${Date.now()}`}
                    src={imageUrl}
                    alt={selectedVisualization}
                    className="visualization-display__image"
                    onClick={(e) => {
                      e.preventDefault();
                      openInNewTab(imageUrl);
                    }}
                  />
                </a>
              )}
              <div className="visualization-display__actions">
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadPng}
                  disabled={!imageUrl}
                >
                  Download PNG
                </button>
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadJson}
                  disabled={!vizJson || jsonLoading}
                  title={modelId ? '' : 'JSON available only for model-specific visualizations'}
                >
                  {jsonLoading ? 'Loading JSON...' : 'Download JSON'}
                </button>
                {modelId && (
                  <button 
                    className="btn btn--primary"
                    onClick={handleExportPdf}
                    disabled={!imageUrl || exportingPdf}
                    title="Export as PDF"
                  >
                    {exportingPdf ? 'Exporting...' : 'Export PDF'}
                  </button>
                )}
              </div>
            </div>
          </InfoTooltip>
        );
      case 'confusion-matrix':
        return (
          <InfoTooltip 
            title="Confusion Matrix" 
            content="Shows the classification model's predictions vs actual values. Diagonal elements represent correct predictions. Off-diagonal elements show misclassifications. Includes accuracy, precision, and recall metrics."
          >
            <div className="visualization-display__image-container">
              {imageLoading && <LoadingState message="Loading confusion matrix image..." />}
              {imageError && <ErrorState message={imageError} />}
              {!imageLoading && !imageError && imageUrl && (
                <a
                  href={imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open image in new tab"
                  onClick={(e) => {
                    e.preventDefault();
                    openInNewTab(imageUrl);
                  }}
                >
                  <img 
                    key={`confusion-matrix-${effectiveDatasetName}-${modelId || modelType}-${Date.now()}`}
                    src={imageUrl}
                    alt={selectedVisualization}
                    className="visualization-display__image"
                    onClick={(e) => {
                      e.preventDefault();
                      openInNewTab(imageUrl);
                    }}
                  />
                </a>
              )}
              <div className="visualization-display__actions">
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadPng}
                  disabled={!imageUrl}
                >
                  Download PNG
                </button>
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadJson}
                  disabled={!vizJson || jsonLoading}
                  title={modelId ? '' : 'JSON available only for model-specific visualizations'}
                >
                  {jsonLoading ? 'Loading JSON...' : 'Download JSON'}
                </button>
                {modelId && (
                  <button 
                    className="btn btn--primary"
                    onClick={handleExportPdf}
                    disabled={!imageUrl || exportingPdf}
                    title="Export as PDF"
                  >
                    {exportingPdf ? 'Exporting...' : 'Export PDF'}
                  </button>
                )}
              </div>
            </div>
          </InfoTooltip>
        );
      case 'training-curve':
        return (
          <InfoTooltip 
            title="Training Curve" 
            content="Shows the model's learning progress over time (Loss vs Epochs). The left graph shows Loss (should decrease). The right graph shows Validation Metric (should improve). The Green dotted line marks the 'Best Epoch'."
          >
          <div className="visualization-context">
            <h4>Understanding Training Curve</h4>
            <ul>
              <li><strong>Training loss:</strong> Shows how well a model fits the training data</li>
              <li><strong>Validation loss:</strong> Measures ability to generalize on unseen data (validation set)</li>
              <li><strong>Early Stopping:</strong> Stops training when validation loss stops improving to prevent overfitting </li>
              <li><strong>Best Epoch:</strong> The epoch with the lowest validation loss, representing epoch with best generalization</li>
            </ul>
            <h4>Identifying Overfitting and Underfitting</h4>
            <ul>
              <li><strong>Overfitting:</strong> Low training loss but high validation loss. Model learns training data too well but fails to generalize.</li>
              <li><strong>Underfitting:</strong> Both training and validation losses are high. Model is too simple to capture underlying patterns.</li>
              <li><strong>Good Fit:</strong> Both training and validation losses decrease together, indicating effective learning and generalization.</li>
            </ul>
          </div>
            <div className="visualization-display__image-container">
              {imageLoading && <LoadingState message="Loading training curve..." />}
              {imageError && <ErrorState message={imageError} />}
              {!imageLoading && !imageError && imageUrl && (
                <a
                  href={imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    openInNewTab(imageUrl);
                  }}
                >
                  <img 
                    key={`training-curve-${effectiveDatasetName}-${modelId}-${Date.now()}`}
                    src={imageUrl}
                    alt="Training Curve"
                    className="visualization-display__image"
                    onError={() => setError('Failed to load training curve. It might not be available for this model type.')}
                  />
                </a>
              )}
              <div className="visualization-display__actions">
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadPng}
                  disabled={!imageUrl}
                >
                  Download PNG
                </button>
                {modelId && (
                  <button 
                    className="btn btn--primary"
                    onClick={handleExportPdf}
                    disabled={!imageUrl || exportingPdf}
                  >
                    {exportingPdf ? 'Exporting...' : 'Export PDF'}
                  </button>
                )}
              </div>
            </div>
          </InfoTooltip>
        );
      case 'instance-comparison':
        return (
          <InfoTooltip 
            title="Instance Comparison (AD vs Baseline)" 
            content="Shows the comparison of model confidence scores for known Alzheimer-associated genes versus a randomly sampled baseline. A clear separation indicates the model has successfully learned biological signals rather than random noise."
          >
            <div className="visualization-display__image-container">
              {imageLoading && <LoadingState message="Loading instance comparison chart..." />}
              {imageError && <ErrorState message={imageError} />}
              {!imageLoading && !imageError && imageUrl && (
                <a
                  href={imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    openInNewTab(imageUrl);
                  }}
                >
                  <img 
                    key={`instance-comparison-${effectiveDatasetName}-${modelId}-${Date.now()}`}
                    src={imageUrl}
                    alt="Instance Comparison"
                    className="visualization-display__image"
                    onError={() => setError('Failed to load instance comparison chart. Ensure the validation script has run successfully for this model.')}
                  />
                </a>
              )}
              <div className="visualization-display__actions">
                <button 
                  className="btn btn--secondary"
                  onClick={handleDownloadPng}
                  disabled={!imageUrl}
                >
                  Download PNG
                </button>
                {modelId && (
                  <button 
                    className="btn btn--primary"
                    onClick={handleExportPdf}
                    disabled={!imageUrl || exportingPdf}
                  >
                    {exportingPdf ? 'Exporting...' : 'Export PDF'}
                  </button>
                )}
              </div>
            </div>
          </InfoTooltip>
        );
      default:
        return null;
    }
  };

  return (
    <div className="visualization-display">
      <div className="visualization-display__header">
        <div className="visualization-display__selector">
          <label className="visualization-display__selector-label" id="viz-select-label" htmlFor="viz-select">
            Select Visualization:
          </label>
          <select 
            id="viz-select"
            aria-labelledby="viz-select-label"
            value={selectedVisualization} 
            onChange={e => setSelectedVisualization(e.target.value)}
            className="visualization-display__selector-dropdown"
            title="Choose a visualization type to explore your data and model results"
          >
            <option value="">Choose a visualization...</option>
            {availableVisualizations.map(viz => (
              <option key={viz.key} value={viz.key} title={getVisualizationDescription(viz.key)}>
                {viz.label}
              </option>
            ))}
          </select>
        </div>
        
        {modelId && modelType && (
          <button 
            className="btn btn--primary visualization-display__export-all-btn"
            onClick={handleExportComprehensive}
            disabled={exportingPdf}
            title="Export all available visualizations as a comprehensive PDF report"
          >
            {exportingPdf ? 'Exporting Report...' : 'Export Full Report (PDF)'}
          </button>
        )}
      </div>

      {exportSuccess && (
        <div className="visualization-display__message visualization-display__message--success">
          {exportSuccess}
        </div>
      )}

      {selectedVisualization ? (
        <div className="visualization-display__content" key={`${selectedVisualization}-${effectiveDatasetName}-${modelId || modelType}`}>
          <div className="visualization-display__content-header">
            <h3 className="visualization-display__content-title">{availableVisualizations.find(v => v.key === selectedVisualization)?.label}</h3>
            {(selectedVisualization === 'roc-curve' 
              || selectedVisualization === 'confusion-matrix'
              || selectedVisualization === 'feature-importance'
              || selectedVisualization === 'model-performance') && (
              <button 
                className="visualization-display__generate-button"
                onClick={() => generateVisualization(selectedVisualization)}
                disabled={isGenerating}
                title={`Generate ${selectedVisualization} for ${effectiveDatasetName} with ${modelId || modelType}`}
              >
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
            )}
          </div>
          
          {generationMessage && (
            <div className={`visualization-display__message ${generationMessage.includes('successfully') ? 'success' : 'info'}`}>
              {generationMessage}
            </div>
          )}
          
          {renderVisualization()}
        </div>
      ) : (
        <EmptyState 
          message={`No visualizations available for dataset "${effectiveDatasetName}". To see visualizations:
          
• For correlation heatmaps: Run correlation analysis in the Explore tab
• For distributions: Run data exploration in the Explore tab  
• For cluster scatter: Run K-Means clustering in the Train tab (Unsupervised mode)
• For feature importance/model performance: Train a model first
• For ROC curves/confusion matrices: Train a classification model first, then select and generate`}
          icon="🔍"
        />
      )}
    </div>
  );
}

export default VisualizationDisplay;